import { useCallback, useEffect, useMemo, useState } from 'react';
import Papa from 'papaparse';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/components/ui/use-toast';
import type { MenuBulkData, MenuBulkImportEnvelope } from '@/lib/api/menu';

type CsvFieldId =
  | 'categoryName'
  | 'categorySortOrder'
  | 'itemName'
  | 'itemDescription'
  | 'itemPrice'
  | 'itemTaxRate'
  | 'itemIsActive'
  | 'itemAllergens';

type CsvFieldDefinition = {
  id: CsvFieldId;
  label: string;
  required: boolean;
  description?: string;
  parser?: (value: string) => unknown;
};

const CSV_FIELD_DEFINITIONS: CsvFieldDefinition[] = [
  {
    id: 'categoryName',
    label: 'Category name',
    required: true,
    description: 'Groups items together. Rows with the same value reuse the category.',
  },
  {
    id: 'categorySortOrder',
    label: 'Category sort order',
    required: false,
    description: 'Optional numeric value to control ordering.',
    parser: (value) => Number.parseInt(value, 10),
  },
  {
    id: 'itemName',
    label: 'Item name',
    required: true,
    description: 'Name shown to guests across ordering surfaces.',
  },
  {
    id: 'itemDescription',
    label: 'Item description',
    required: false,
    description: 'Optional guest-facing description. Leave blank to skip.',
  },
  {
    id: 'itemPrice',
    label: 'Item price (e.g. 12.50)',
    required: true,
    parser: (value) => Number.parseFloat(value),
  },
  {
    id: 'itemTaxRate',
    label: 'Tax rate (bps or %)',
    required: false,
    description: 'Accepts 8.25%, 0.0825, or 825 (basis points). Leave blank to keep tenant defaults.',
  },
  {
    id: 'itemIsActive',
    label: 'Active (true/false)',
    required: false,
    description: 'Use true/false, yes/no, or 1/0 to control availability. Defaults to true when empty.',
  },
  {
    id: 'itemAllergens',
    label: 'Allergens (comma or semicolon separated)',
    required: false,
  },
];

type CsvRow = Record<string, string>;

type MappingState = Record<CsvFieldId, string | null>;

type CsvPreview = {
  columns: string[];
  rows: CsvRow[];
};

type RowIssue = {
  rowNumber: number;
  message: string;
};

const csvTemplateStorageKey = (tenantSlug?: string) =>
  tenantSlug ? `menu-csv-template:${tenantSlug}` : 'menu-csv-template';

const normalizeHeader = (header: string) => header.trim().toLowerCase();

const autoDetectMapping = (preview: CsvPreview): MappingState => {
  const headerMap = new Map<string, string>();
  preview.columns.forEach((column) => {
    headerMap.set(normalizeHeader(column), column);
  });

  const mapping: MappingState = {
    categoryName: null,
    categorySortOrder: null,
    itemName: null,
    itemDescription: null,
    itemPrice: null,
    itemTaxRate: null,
    itemIsActive: null,
    itemAllergens: null,
  };

  const detectionHints: Record<CsvFieldId, string[]> = {
    categoryName: ['category', 'category name', 'menu category'],
    categorySortOrder: ['category sort', 'category order'],
    itemName: ['item', 'item name', 'menu item'],
    itemDescription: ['description', 'item description'],
    itemPrice: ['price', 'unit price', 'item price'],
    itemTaxRate: ['tax', 'tax rate', 'tax bps'],
    itemIsActive: ['active', 'is active', 'status'],
    itemAllergens: ['allergens', 'allergen tags', 'allergen'],
  };

  (Object.keys(mapping) as CsvFieldId[]).forEach((fieldId) => {
    const hints = detectionHints[fieldId];
    for (const hint of hints) {
      const match = headerMap.get(normalizeHeader(hint));
      if (match) {
        mapping[fieldId] = match;
        return;
      }
      for (const [normalized, original] of headerMap.entries()) {
        if (normalized.includes(hint)) {
          mapping[fieldId] = original;
          return;
        }
      }
    }
  });

  return mapping;
};

const parseBoolean = (value: string): boolean | undefined => {
  const normalized = value.trim().toLowerCase();
  if (['true', '1', 'yes', 'y'].includes(normalized)) return true;
  if (['false', '0', 'no', 'n'].includes(normalized)) return false;
  return undefined;
};

const parseTaxBps = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.endsWith('%')) {
    const parsed = Number.parseFloat(trimmed.slice(0, -1));
    if (Number.isFinite(parsed)) {
      return Math.round(parsed * 100);
    }
    return null;
  }
  const numeric = Number.parseFloat(trimmed);
  if (!Number.isFinite(numeric)) {
    return null;
  }
  if (numeric <= 1) {
    return Math.round(numeric * 10000);
  }
  if (numeric > 1 && numeric < 100) {
    return Math.round(numeric * 100);
  }
  return Math.round(numeric);
};

const parseAllergens = (value: string): string[] => {
  if (!value.trim()) return [];
  return value
    .split(/[;,]/)
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
};

const formatNumber = (value: number | null | undefined) =>
  value === null || value === undefined ? '-' : value.toLocaleString();

const calculateRowIssues = (
  rows: CsvRow[],
  mapping: MappingState,
): RowIssue[] => {
  const issues: RowIssue[] = [];
  rows.forEach((row, index) => {
    const rowNumber = index + 2;
    CSV_FIELD_DEFINITIONS.forEach((field) => {
      if (!field.required) return;
      const column = mapping[field.id];
      if (!column) {
        return;
      }
      const value = row[column];
      if (!value || value.trim().length === 0) {
        issues.push({
          rowNumber,
          message: `${field.label} is required.`,
        });
      }
    });
  });
  return issues;
};

const transformCsvToBulkData = (
  rows: CsvRow[],
  mapping: MappingState,
): { data: MenuBulkData; issues: RowIssue[] } => {
  const issues: RowIssue[] = [];
  const categories: MenuBulkData['categories'] = [];
  const items: MenuBulkData['items'] = [];

  const categoryIndex = new Map<string, { id: string; sortOrder?: number }>();

  rows.forEach((row, index) => {
    const rowNumber = index + 2;
    const categoryColumn = mapping.categoryName;
    const itemColumn = mapping.itemName;
    const priceColumn = mapping.itemPrice;

    if (!categoryColumn || !itemColumn || !priceColumn) {
      return;
    }

    const categoryName = row[categoryColumn]?.trim() ?? '';
    const itemName = row[itemColumn]?.trim() ?? '';
    const priceRaw = row[priceColumn]?.trim() ?? '';

    if (!categoryName || !itemName || !priceRaw) {
      issues.push({
        rowNumber,
        message: 'Category, item name, and price are required.',
      });
      return;
    }

    const parsedPrice = Number.parseFloat(priceRaw.replace(/[$,]/g, ''));
    if (!Number.isFinite(parsedPrice)) {
      issues.push({
        rowNumber,
        message: `Invalid price value "${priceRaw}".`,
      });
      return;
    }

    let categoryRecord = categoryIndex.get(categoryName);
    if (!categoryRecord) {
      const sortOrderColumn = mapping.categorySortOrder;
      const sortValueRaw = sortOrderColumn ? row[sortOrderColumn] : undefined;
      const sortOrder = sortValueRaw
        ? Number.parseInt(sortValueRaw, 10)
        : undefined;
      const categoryId = globalThis.crypto?.randomUUID
        ? globalThis.crypto.randomUUID()
        : `cat-${categories.length + 1}`;
      categoryRecord = { id: categoryId, sortOrder: Number.isFinite(sortOrder) ? sortOrder : undefined };
      categories.push({
        id: categoryId,
        name: categoryName,
        sortOrder: categoryRecord.sortOrder ?? categories.length,
      });
      categoryIndex.set(categoryName, categoryRecord);
    }

    const descriptionColumn = mapping.itemDescription;
    const taxColumn = mapping.itemTaxRate;
    const activeColumn = mapping.itemIsActive;
    const allergenColumn = mapping.itemAllergens;

    const parsedTax =
      taxColumn && row[taxColumn]
        ? parseTaxBps(row[taxColumn]!)
        : null;
    const isActive =
      activeColumn && row[activeColumn]
        ? parseBoolean(row[activeColumn]!)
        : undefined;

    const allergenTags =
      allergenColumn && row[allergenColumn]
        ? parseAllergens(row[allergenColumn]!)
        : [];

    const itemId = globalThis.crypto?.randomUUID
      ? globalThis.crypto.randomUUID()
      : `item-${items.length + 1}`;

    items.push({
      id: itemId,
      categoryId: categoryRecord.id,
      name: itemName,
      description: descriptionColumn ? row[descriptionColumn] ?? null : null,
      priceCents: Math.round(parsedPrice * 100),
      taxRateBps: parsedTax ?? undefined,
      sortOrder: items.length,
      isActive: isActive ?? true,
      allergenTags,
    });
  });

  return {
    data: {
      categories,
      items,
      modifierGroups: [],
    },
    issues,
  };
};

type BulkImportDialogProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: MenuBulkImportEnvelope) => Promise<void> | void;
  isSubmitting?: boolean;
  tenantSlug?: string;
};

export function BulkImportDialog({
  isOpen,
  onOpenChange,
  onSubmit,
  isSubmitting = false,
  tenantSlug,
}: BulkImportDialogProps): JSX.Element {
  const { t } = useTranslation('menu');
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<'upload' | 'map' | 'review'>('upload');
  const [selectedFormat, setSelectedFormat] = useState<'csv' | 'json' | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [csvPreview, setCsvPreview] = useState<CsvPreview | null>(null);
  const [csvMapping, setCsvMapping] = useState<MappingState>(() => ({
    categoryName: null,
    categorySortOrder: null,
    itemName: null,
    itemDescription: null,
    itemPrice: null,
    itemTaxRate: null,
    itemIsActive: null,
    itemAllergens: null,
  }));
  const [useSavedTemplate, setUseSavedTemplate] = useState<boolean>(false);
  const [reviewData, setReviewData] = useState<MenuBulkData | null>(null);
  const [rowIssues, setRowIssues] = useState<RowIssue[]>([]);
  const [mappingName, setMappingName] = useState<string>('');
  const [isSavingTemplate, setIsSavingTemplate] = useState<boolean>(false);

  const csvFieldCopy = useMemo(() => {
    const labels = {} as Record<CsvFieldId, { label: string; helper: string }>;
    CSV_FIELD_DEFINITIONS.forEach((field) => {
      labels[field.id] = {
        label: t(`bulkImport.instructions.columns.${field.id}.label`, {
          defaultValue: field.label,
        }),
        helper: t(`bulkImport.instructions.columns.${field.id}.helper`, {
          defaultValue: field.description ?? '',
        }),
      };
    });
    return labels;
  }, [t]);

  const csvInstructionFields = useMemo(
    () =>
      CSV_FIELD_DEFINITIONS.map((field) => ({
        id: field.id,
        required: field.required,
        label: csvFieldCopy[field.id].label,
        helper: csvFieldCopy[field.id].helper,
      })),
    [csvFieldCopy],
  );

  const requiredBadgeLabel = t('bulkImport.instructions.requiredBadge', { defaultValue: 'Required' });
  const optionalBadgeLabel = t('bulkImport.instructions.optionalBadge', { defaultValue: 'Optional' });

  const resetState = useCallback(() => {
    setActiveTab('upload');
    setSelectedFormat(null);
    setFileName('');
    setCsvPreview(null);
    setCsvMapping({
      categoryName: null,
      categorySortOrder: null,
      itemName: null,
      itemDescription: null,
      itemPrice: null,
      itemTaxRate: null,
      itemIsActive: null,
      itemAllergens: null,
    });
    setUseSavedTemplate(false);
    setReviewData(null);
    setRowIssues([]);
    setMappingName('');
  }, []);

  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen, resetState]);

  const savedTemplate = useMemo(() => {
    try {
      const raw = window.localStorage.getItem(csvTemplateStorageKey(tenantSlug));
      if (!raw) return null;
      const parsed = JSON.parse(raw) as {
        mapping: MappingState;
        name?: string;
      };
      return parsed;
    } catch {
      return null;
    }
  }, [tenantSlug]);

  useEffect(() => {
    if (csvPreview) {
      if (savedTemplate?.mapping && useSavedTemplate) {
        setCsvMapping(savedTemplate.mapping);
        setMappingName(savedTemplate.name ?? '');
      } else {
        const mapping = autoDetectMapping(csvPreview);
        setCsvMapping(mapping);
      }
    }
  }, [csvPreview, savedTemplate?.mapping, savedTemplate?.name, useSavedTemplate]);

  const handleFileSelection = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;
      const file = files[0]!;
      setFileName(file.name);
      const extension = file.name.split('.').pop()?.toLowerCase();

      if (extension === 'json') {
        setSelectedFormat('json');
        setActiveTab('review');
        file
          .text()
          .then((text) => JSON.parse(text) as MenuBulkData)
          .then((data) => {
            setReviewData(data);
            setRowIssues([]);
          })
          .catch((error) => {
            console.error('[BulkImportDialog] Failed to parse JSON', error);
            toast({
              title: 'Invalid JSON file',
              description: 'Check the structure and try again.',
              variant: 'destructive',
            });
            setReviewData(null);
          });
        return;
      }

      setSelectedFormat('csv');
      setActiveTab('map');
      Papa.parse<CsvRow>(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header) => header.trim(),
        complete: (result) => {
          if (result.errors.length > 0) {
            toast({
              title: 'Unable to parse CSV',
              description: result.errors[0]?.message ?? 'Check the file contents and try again.',
              variant: 'destructive',
            });
            setCsvPreview(null);
            return;
          }
          const rows = result.data ?? [];
          const columns = result.meta.fields ?? [];
          if (columns.length === 0) {
            toast({
              title: 'CSV missing headers',
              description: 'Ensure the first row contains column names.',
              variant: 'destructive',
            });
            setCsvPreview(null);
            return;
          }
          setCsvPreview({ columns, rows });
        },
        error: (error) => {
          console.error('[BulkImportDialog] CSV parse failed', error);
          toast({
            title: 'Unable to parse CSV',
            description: error.message,
            variant: 'destructive',
          });
          setCsvPreview(null);
        },
      });
    },
    [toast],
  );

  const handleBuildReview = useCallback(() => {
    if (!csvPreview) return;
    const issues = calculateRowIssues(csvPreview.rows, csvMapping);
    const { data, issues: transformIssues } = transformCsvToBulkData(csvPreview.rows, csvMapping);
    const combinedIssues = [...issues, ...transformIssues];
    setReviewData(data);
    setRowIssues(combinedIssues);
    setActiveTab('review');
  }, [csvMapping, csvPreview]);

  const handleSaveTemplate = useCallback(() => {
    if (!csvPreview) return;
    setIsSavingTemplate(true);
    try {
      window.localStorage.setItem(
        csvTemplateStorageKey(tenantSlug),
        JSON.stringify({
          mapping: csvMapping,
          name: mappingName,
          updatedAt: Date.now(),
        }),
      );
      toast({
        title: 'Template saved',
        description: 'Your column mapping is ready for the next import.',
      });
    } catch (error) {
      console.error('[BulkImportDialog] Failed to save template', error);
      toast({
        title: 'Unable to save template',
        description: 'Storage may be disabled. Try again later.',
        variant: 'destructive',
      });
    } finally {
      setIsSavingTemplate(false);
    }
  }, [csvMapping, mappingName, tenantSlug, toast, csvPreview]);

  const handleSubmit = useCallback(async () => {
    if (!reviewData || !selectedFormat) return;
    const envelope: MenuBulkImportEnvelope = selectedFormat === 'csv'
      ? {
          format: 'csv',
          sourceFilename: fileName,
          mappingName: mappingName || undefined,
          metadata: {
            columnMapping: csvMapping,
            rowCount: reviewData.items.length,
          },
          data: reviewData,
        }
      : {
          format: 'json',
          sourceFilename: fileName,
          data: reviewData,
        };

    try {
      await onSubmit(envelope);
    } catch (error) {
      // Errors are surfaced by the caller; avoid bubbling the rejection to React.
      console.error('[BulkImportDialog] submit failed', error);
    }
  }, [csvMapping, fileName, mappingName, onSubmit, reviewData, selectedFormat]);

  const totalCategories = reviewData?.categories.length ?? 0;
  const totalItems = reviewData?.items.length ?? 0;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bulk import menu</DialogTitle>
          <DialogDescription>
            Upload JSON exports or map CSV columns to refresh categories and items in bulk.
          </DialogDescription>
        </DialogHeader>
        <Tabs value={activeTab} className="space-y-4" onValueChange={(value) => setActiveTab(value as typeof activeTab)}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="upload">1. Upload</TabsTrigger>
            <TabsTrigger value="map" disabled={selectedFormat !== 'csv'}>2. Map columns</TabsTrigger>
            <TabsTrigger value="review" disabled={!reviewData}>3. Review</TabsTrigger>
          </TabsList>
          <TabsContent value="upload">
            <div className="space-y-3">
              <Label>Select file</Label>
              <Input
                type="file"
                accept=".json,.csv"
                onChange={(event) => handleFileSelection(event.target.files)}
              />
              <p className="text-xs text-muted-foreground">
                JSON exports are applied immediately. CSV uploads require mapping before import.
              </p>
              {fileName ? (
                <p className="text-sm text-muted-foreground">
                  Selected: <span className="font-medium text-foreground">{fileName}</span>
                </p>
              ) : null}
              {savedTemplate ? (
                <div className="flex items-center gap-3 rounded-md border border-dashed border-muted-foreground/40 p-3">
                  <input
                    id="use-template"
                    type="checkbox"
                    className="h-4 w-4 rounded border-border"
                    checked={useSavedTemplate}
                    onChange={(event) => setUseSavedTemplate(event.target.checked)}
                  />
                  <div className="space-y-1">
                    <Label htmlFor="use-template" className="font-medium">
                      Use saved mapping{savedTemplate.name ? ` (${savedTemplate.name})` : ''}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Applies the previously saved column mapping for this tenant.
                    </p>
                  </div>
                </div>
              ) : null}
              <Separator />
              <Alert>
                <AlertTitle>
                  {t('bulkImport.instructions.title', { defaultValue: 'CSV requirements' })}
                </AlertTitle>
                <AlertDescription>
                  <p className="text-sm text-muted-foreground">
                    {t('bulkImport.instructions.description', {
                      defaultValue: 'Include a header row and the following columns before importing your menu:',
                    })}
                  </p>
                  <div className="mt-3 grid gap-2">
                    {csvInstructionFields.map((field) => (
                      <div
                        key={field.id}
                        className="rounded-md border border-border bg-muted/40 p-3"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-foreground">{field.label}</span>
                          <Badge variant={field.required ? 'destructive' : 'outline'}>
                            {field.required ? requiredBadgeLabel : optionalBadgeLabel}
                          </Badge>
                        </div>
                        {field.helper ? (
                          <p className="mt-1 text-xs text-muted-foreground">{field.helper}</p>
                        ) : null}
                      </div>
                    ))}
                  </div>
                  <p className="mt-3 text-xs text-muted-foreground">
                    {t('bulkImport.instructions.footnote', {
                      defaultValue:
                        'Optional columns can be left blank. Imports overwrite the menu data for this restaurant once submitted.',
                    })}
                  </p>
                </AlertDescription>
              </Alert>
            </div>
          </TabsContent>
          <TabsContent value="map">
            {csvPreview ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-foreground">Column mapping</h4>
                    <p className="text-xs text-muted-foreground">
                      Map CSV columns to menu fields. Required fields must be mapped before continuing.
                    </p>
                  </div>
                  <Button size="sm" variant="outline" onClick={handleBuildReview}>
                    Continue to review
                  </Button>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  {CSV_FIELD_DEFINITIONS.map((field) => (
                    <div key={field.id} className="space-y-2">
                      <Label className="flex items-center gap-2">
                        {csvFieldCopy[field.id].label}
                        <Badge variant={field.required ? 'secondary' : 'outline'}>
                          {field.required ? requiredBadgeLabel : optionalBadgeLabel}
                        </Badge>
                      </Label>
                      <Select
                        value={csvMapping[field.id] ?? 'none'}
                        onValueChange={(value) =>
                          setCsvMapping((prev) => ({
                            ...prev,
                            [field.id]: value === 'none' ? null : value,
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select column" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Unmapped</SelectItem>
                          {csvPreview.columns.map((column) => (
                            <SelectItem key={column} value={column}>
                              {column}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {csvFieldCopy[field.id].helper ? (
                        <p className="text-xs text-muted-foreground">{csvFieldCopy[field.id].helper}</p>
                      ) : null}
                    </div>
                  ))}
                </div>
                <Separator />
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="mapping-name">Template name</Label>
                    <Input
                      id="mapping-name"
                      placeholder="Optional label (e.g., 'DoorDash export')"
                      value={mappingName}
                      onChange={(event) => setMappingName(event.target.value)}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    className="self-start sm:self-auto"
                    onClick={handleSaveTemplate}
                    disabled={isSavingTemplate}
                  >
                    {isSavingTemplate ? 'Saving…' : 'Save template'}
                  </Button>
                </div>
                <Separator />
                <div>
                  <h4 className="text-sm font-medium text-foreground mb-2">Preview</h4>
                  <ScrollArea className="h-48 rounded-md border">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/40">
                        <tr>
                          {csvPreview.columns.map((column) => (
                            <th key={column} className="px-3 py-2 text-left font-medium">
                              {column}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {csvPreview.rows.slice(0, 5).map((row, rowIndex) => (
                          <tr key={rowIndex} className="border-t">
                            {csvPreview.columns.map((column) => (
                              <td key={column} className="px-3 py-2 text-muted-foreground">
                                {row[column] ?? ''}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </ScrollArea>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Upload a CSV file to configure column mapping.
              </p>
            )}
          </TabsContent>
          <TabsContent value="review">
            {reviewData ? (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                  <Badge variant="outline">{selectedFormat?.toUpperCase() ?? 'JSON'}</Badge>
                  {fileName ? <Badge variant="outline">File: {fileName}</Badge> : null}
                  <Badge variant="outline">Categories: {formatNumber(totalCategories)}</Badge>
                  <Badge variant="outline">Items: {formatNumber(totalItems)}</Badge>
                </div>
                {rowIssues.length > 0 ? (
                  <Alert variant="destructive">
                    <AlertTitle>Review required</AlertTitle>
                    <AlertDescription>
                      Resolve the following issues before importing:
                      <ul className="mt-2 list-disc space-y-1 pl-5 text-xs">
                        {rowIssues.slice(0, 5).map((issue, index) => (
                          <li key={`${issue.rowNumber}-${index}`}>
                            Row {issue.rowNumber}: {issue.message}
                          </li>
                        ))}
                        {rowIssues.length > 5 ? (
                          <li>…and {rowIssues.length - 5} more</li>
                        ) : null}
                      </ul>
                    </AlertDescription>
                  </Alert>
                ) : null}
                <div>
                  <h4 className="text-sm font-medium text-foreground mb-2">Sample items</h4>
                  <ScrollArea className="h-48 rounded-md border">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/40">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium">Category</th>
                          <th className="px-3 py-2 text-left font-medium">Item</th>
                          <th className="px-3 py-2 text-left font-medium">Price</th>
                          <th className="px-3 py-2 text-left font-medium">Active</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reviewData.items.slice(0, 8).map((item) => {
                          const category = reviewData.categories.find((entry) => entry.id === item.categoryId);
                          return (
                            <tr key={item.id} className="border-t">
                              <td className="px-3 py-2 text-muted-foreground">{category?.name ?? '-'}</td>
                              <td className="px-3 py-2 text-muted-foreground">{item.name}</td>
                              <td className="px-3 py-2 text-muted-foreground">
                                ${(item.priceCents / 100).toFixed(2)}
                              </td>
                              <td className="px-3 py-2 text-muted-foreground">{item.isActive ? 'Yes' : 'No'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </ScrollArea>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Upload a JSON or CSV file to review the normalized payload.
              </p>
            )}
          </TabsContent>
        </Tabs>
        <DialogFooter className="flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-xs text-muted-foreground">
            Imports replace existing menu records. Make sure the mapping looks correct before continuing.
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Button variant="outline" onClick={() => setActiveTab('upload')} disabled={isSubmitting}>
              Back
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!reviewData || rowIssues.length > 0 || isSubmitting}
            >
              {isSubmitting ? 'Importing…' : 'Start import'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
